import React, { useState } from 'react';
import { 
  Shield, 
  Save, 
  X, 
  ArrowLeft,
  Check,
  Users,
  Settings,
  FileText,
  ShoppingCart,
  Palette,
  Eye,
  EyeOff
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

const AddRole = () => {
  const navigate = useNavigate();
  const [newRole, setNewRole] = useState({
    name: '',
    description: '',
    status: 'active',
    permissions: []
  });

  // Available permissions grouped by category
  const availablePermissions = {
    'users': {
      name: 'Quản lý người dùng',
      icon: Users,
      permissions: [
        { id: 'users.read', name: 'Xem danh sách người dùng', description: 'Có thể xem thông tin người dùng' },
        { id: 'users.write', name: 'Thêm/Sửa người dùng', description: 'Có thể tạo và chỉnh sửa người dùng' },
        { id: 'users.delete', name: 'Xóa người dùng', description: 'Có thể xóa người dùng khỏi hệ thống' },
        { id: 'users.approve', name: 'Phê duyệt người dùng', description: 'Có thể phê duyệt hoặc từ chối đăng ký' }
      ]
    },
    'roles': {
      name: 'Quản lý vai trò',
      icon: Shield,
      permissions: [
        { id: 'roles.read', name: 'Xem vai trò', description: 'Có thể xem danh sách vai trò' },
        { id: 'roles.write', name: 'Thêm/Sửa vai trò', description: 'Có thể tạo và chỉnh sửa vai trò' },
        { id: 'roles.delete', name: 'Xóa vai trò', description: 'Có thể xóa vai trò khỏi hệ thống' }
      ]
    },
    'products': {
      name: 'Quản lý sản phẩm',
      icon: ShoppingCart,
      permissions: [
        { id: 'products.read', name: 'Xem sản phẩm', description: 'Có thể xem danh sách sản phẩm' },
        { id: 'products.write', name: 'Thêm/Sửa sản phẩm', description: 'Có thể tạo và chỉnh sửa sản phẩm' },
        { id: 'products.delete', name: 'Xóa sản phẩm', description: 'Có thể xóa sản phẩm khỏi hệ thống' },
        { id: 'products.approve', name: 'Phê duyệt sản phẩm', description: 'Có thể phê duyệt sản phẩm trước khi hiển thị' }
      ]
    },
    'orders': {
      name: 'Quản lý đơn hàng',
      icon: FileText,
      permissions: [
        { id: 'orders.read', name: 'Xem đơn hàng', description: 'Có thể xem danh sách đơn hàng' },
        { id: 'orders.write', name: 'Thêm/Sửa đơn hàng', description: 'Có thể tạo và chỉnh sửa đơn hàng' },
        { id: 'orders.delete', name: 'Xóa đơn hàng', description: 'Có thể xóa đơn hàng khỏi hệ thống' },
        { id: 'orders.process', name: 'Xử lý đơn hàng', description: 'Có thể xử lý và cập nhật trạng thái đơn hàng' }
      ]
    },
    'design': {
      name: 'Quản lý thiết kế',
      icon: Palette,
      permissions: [
        { id: 'design.read', name: 'Xem thiết kế', description: 'Có thể xem các thiết kế hiện có' },
        { id: 'design.write', name: 'Thêm/Sửa thiết kế', description: 'Có thể tạo và chỉnh sửa thiết kế' },
        { id: 'design.delete', name: 'Xóa thiết kế', description: 'Có thể xóa thiết kế khỏi hệ thống' },
        { id: 'design.approve', name: 'Phê duyệt thiết kế', description: 'Có thể phê duyệt thiết kế trước khi sử dụng' }
      ]
    },
    'content': {
      name: 'Quản lý nội dung',
      icon: FileText,
      permissions: [
        { id: 'content.read', name: 'Xem nội dung', description: 'Có thể xem nội dung hệ thống' },
        { id: 'content.write', name: 'Thêm/Sửa nội dung', description: 'Có thể tạo và chỉnh sửa nội dung' },
        { id: 'content.delete', name: 'Xóa nội dung', description: 'Có thể xóa nội dung khỏi hệ thống' },
        { id: 'content.moderate', name: 'Kiểm duyệt nội dung', description: 'Có thể kiểm duyệt nội dung trước khi xuất bản' }
      ]
    },
    'system': {
      name: 'Quản lý hệ thống',
      icon: Settings,
      permissions: [
        { id: 'system.read', name: 'Xem cài đặt hệ thống', description: 'Có thể xem cài đặt hệ thống' },
        { id: 'system.write', name: 'Cập nhật cài đặt', description: 'Có thể thay đổi cài đặt hệ thống' },
        { id: 'system.admin', name: 'Quản trị toàn bộ', description: 'Có tất cả quyền hạn trong hệ thống' },
        { id: 'system.logs', name: 'Xem nhật ký hệ thống', description: 'Có thể xem nhật ký hoạt động hệ thống' }
      ]
    }
  };

  const handleAddRole = async () => {
    if (!newRole.name.trim()) {
      toast.error('Vui lòng nhập tên vai trò');
      return;
    }

    if (!newRole.description.trim()) {
      toast.error('Vui lòng nhập mô tả vai trò');
      return;
    }

    if (newRole.permissions.length === 0) {
      toast.error('Vui lòng chọn ít nhất một quyền hạn');
      return;
    }

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      toast.success('Thêm vai trò thành công!');
      navigate('/admin/roles');
    } catch (error) {
      toast.error('Có lỗi xảy ra khi thêm vai trò');
    }
  };

  const handlePermissionChange = (permissionId) => {
    setNewRole(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permissionId)
        ? prev.permissions.filter(id => id !== permissionId)
        : [...prev.permissions, permissionId]
    }));
  };

  const handleSelectAllCategory = (category) => {
    const categoryPermissions = availablePermissions[category].permissions.map(p => p.id);
    const allSelected = categoryPermissions.every(p => newRole.permissions.includes(p));
    
    setNewRole(prev => ({
      ...prev,
      permissions: allSelected
        ? prev.permissions.filter(p => !categoryPermissions.includes(p))
        : [...new Set([...prev.permissions, ...categoryPermissions])]
    }));
  };

  const isCategorySelected = (category) => {
    const categoryPermissions = availablePermissions[category].permissions.map(p => p.id);
    return categoryPermissions.every(p => newRole.permissions.includes(p));
  };

  const isCategoryPartiallySelected = (category) => {
    const categoryPermissions = availablePermissions[category].permissions.map(p => p.id);
    const selectedCount = categoryPermissions.filter(p => newRole.permissions.includes(p)).length;
    return selectedCount > 0 && selectedCount < categoryPermissions.length;
  };

  const getSelectedPermissionsCount = () => {
    return newRole.permissions.length;
  };

  const getTotalPermissionsCount = () => {
    return Object.values(availablePermissions).reduce((total, category) => {
      return total + category.permissions.length;
    }, 0);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto p-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-4 mb-4">
            <button
              onClick={() => navigate('/admin/roles')}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Quay lại</span>
            </button>
          </div>
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Shield className="w-8 h-8 text-blue-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Thêm Vai Trò Mới</h1>
              <p className="text-gray-600 mt-1">Tạo vai trò mới với các quyền hạn phù hợp</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Form Section */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-lg border p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Thông Tin Vai Trò</h2>
              
              <div className="space-y-6">
                {/* Role Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tên vai trò *
                  </label>
                  <input
                    type="text"
                    value={newRole.name}
                    onChange={(e) => setNewRole(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Nhập tên vai trò..."
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mô tả *
                  </label>
                  <textarea
                    value={newRole.description}
                    onChange={(e) => setNewRole(prev => ({ ...prev, description: e.target.value }))}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Mô tả chức năng của vai trò..."
                  />
                </div>

                {/* Status */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Trạng thái
                  </label>
                  <select
                    value={newRole.status}
                    onChange={(e) => setNewRole(prev => ({ ...prev, status: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="active">Hoạt động</option>
                    <option value="inactive">Đã khóa</option>
                  </select>
                </div>

                {/* Summary */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-gray-700 mb-3">Tóm Tắt</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Quyền đã chọn:</span>
                      <span className="font-medium text-blue-600">
                        {getSelectedPermissionsCount()}/{getTotalPermissionsCount()}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Trạng thái:</span>
                      <span className={`font-medium ${newRole.status === 'active' ? 'text-green-600' : 'text-red-600'}`}>
                        {newRole.status === 'active' ? 'Hoạt động' : 'Đã khóa'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Quick Tips */}
                <div className="bg-blue-50 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-blue-800 mb-2">💡 Mẹo</h3>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>• Chọn quyền phù hợp với chức năng của vai trò</li>
                    <li>• Có thể chọn tất cả quyền trong một danh mục</li>
                    <li>• Quyền "Quản trị toàn bộ" cho phép tất cả thao tác</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Permissions Section */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-lg border p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Quyền Hạn</h2>
                <div className="text-sm text-gray-600">
                  Đã chọn: {getSelectedPermissionsCount()}/{getTotalPermissionsCount()} quyền
                </div>
              </div>

              <div className="space-y-6">
                {Object.entries(availablePermissions).map(([categoryKey, category]) => {
                  const IconComponent = category.icon;
                  const isSelected = isCategorySelected(categoryKey);
                  const isPartiallySelected = isCategoryPartiallySelected(categoryKey);

                  return (
                    <div key={categoryKey} className="border border-gray-200 rounded-lg">
                      {/* Category Header */}
                      <div className="flex items-center justify-between p-4 bg-gray-50 border-b border-gray-200">
                        <div className="flex items-center space-x-3">
                          <IconComponent className="w-5 h-5 text-gray-600" />
                          <h3 className="font-medium text-gray-900">{category.name}</h3>
                        </div>
                        <button
                          onClick={() => handleSelectAllCategory(categoryKey)}
                          className={`px-3 py-1 text-sm rounded-md transition-colors ${
                            isSelected
                              ? 'bg-blue-600 text-white'
                              : isPartiallySelected
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          {isSelected ? 'Bỏ chọn tất cả' : 'Chọn tất cả'}
                        </button>
                      </div>

                      {/* Permissions List */}
                      <div className="p-4 space-y-3">
                        {category.permissions.map((permission) => (
                          <div key={permission.id} className="flex items-start space-x-3">
                            <input
                              type="checkbox"
                              id={permission.id}
                              checked={newRole.permissions.includes(permission.id)}
                              onChange={() => handlePermissionChange(permission.id)}
                              className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <div className="flex-1">
                              <label htmlFor={permission.id} className="block text-sm font-medium text-gray-900 cursor-pointer">
                                {permission.name}
                              </label>
                              <p className="text-sm text-gray-600 mt-1">{permission.description}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-8 flex justify-end space-x-4">
          <button
            onClick={() => navigate('/admin/roles')}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Hủy
          </button>
          <button
            onClick={handleAddRole}
            className="flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Save className="w-4 h-4" />
            <span>Lưu Vai Trò</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddRole; 